import {
  SplitSquareHorizontal,
  SplitSquareVertical,
  Link2,
  ArrowLeftRight,
  LogOut,
  BarChart2,
  Film,
} from "lucide-react";
import { Switch } from "./ui/switch";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../contexts/AuthContext";

export default function TopBar({
  layout,
  setLayout,
  syncEnabled,
  setSyncEnabled,
  onSwap,
  canSwap,
  onShowAnalytics,
  onStartExport,
}) {
  const toggleLayout = () =>
    setLayout(layout === "horizontal" ? "vertical" : "horizontal");

  const { user } = useAuth();
  const handleLogout = () => supabase.auth.signOut();

  return (
    <header
      className="h-16 bg-[#0D0D0D] border-b border-white/10 flex items-center justify-between px-4 md:px-6 shrink-0"
      data-testid="top-bar"
    >
      {/* Logo */}
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-md bg-[#007AFF] flex items-center justify-center">
          <span className="font-display text-white text-lg leading-none">
            DV
          </span>
        </div>
        <div className="hidden sm:block">
          <h1
            className="font-display text-xl md:text-2xl uppercase tracking-tight leading-none"
            data-testid="app-title"
          >
            DualView
          </h1>
          <p className="text-[10px] uppercase tracking-[0.2em] text-white/50 mt-0.5">
            Powered by <span className="text-[#007AFF] font-black">Crown Analyzer</span>
          </p>
        </div>
      </div>

      {/* Center controls */}
      <div className="flex items-center gap-2 md:gap-3">
        <button
          onClick={toggleLayout}
          className="flex items-center gap-2 px-3 md:px-4 h-10 rounded-md border border-white/15 bg-white/[0.03] hover:bg-white/[0.08] transition-colors text-xs uppercase tracking-[0.15em] font-bold"
          data-testid="layout-toggle-button"
          title="Toggle layout (L)"
        >
          {layout === "horizontal" ? (
            <SplitSquareHorizontal className="w-4 h-4" />
          ) : (
            <SplitSquareVertical className="w-4 h-4" />
          )}
          <span className="hidden sm:inline">
            {layout === "horizontal" ? "Horizontal" : "Vertical"}
          </span>
        </button>

        <div
          className="flex items-center gap-2 px-3 md:px-4 h-10 rounded-md border border-white/15 bg-white/[0.03]"
          data-testid="sync-toggle-container"
        >
          <Link2
            className={`w-4 h-4 transition-colors ${
              syncEnabled ? "text-[#007AFF]" : "text-white/50"
            }`}
          />
          <span className="text-xs uppercase tracking-[0.15em] font-bold hidden sm:inline">
            Sync
          </span>
          <Switch
            checked={syncEnabled}
            onCheckedChange={setSyncEnabled}
            data-testid="sync-toggle-switch"
            className="data-[state=checked]:bg-[#007AFF]"
          />
        </div>

        <button
          onClick={onSwap}
          disabled={!canSwap}
          className="hidden sm:flex items-center gap-2 px-3 h-10 rounded-md border border-white/15 bg-white/[0.03] hover:bg-white/[0.08] transition-colors text-xs uppercase tracking-[0.15em] font-bold disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="swap-videos-button"
          title="Swap A and B"
        >
          <ArrowLeftRight className="w-4 h-4" />
          <span className="hidden md:inline">Swap</span>
        </button>
      </div>

      {/* Hotkeys hint + logout */}
      <div className="flex items-center gap-3">
        <div className="hidden lg:flex items-center gap-3 text-[10px] uppercase tracking-[0.15em] text-white/40 font-mono">
          <span>
            <kbd className="px-1.5 py-0.5 rounded border border-white/15 bg-white/[0.03] text-white/70">
              Space
            </kbd>{" "}
            Play
          </span>
          <span>
            <kbd className="px-1.5 py-0.5 rounded border border-white/15 bg-white/[0.03] text-white/70">
              ←→
            </kbd>{" "}
            Frame
          </span>
          <span>
            <kbd className="px-1.5 py-0.5 rounded border border-white/15 bg-white/[0.03] text-white/70">
              L
            </kbd>{" "}
            Layout
          </span>
        </div>
        <button
            onClick={onStartExport}
            className="flex items-center gap-2 px-3 h-10 rounded-md border border-white/15 bg-white/[0.03] hover:bg-white/[0.08] transition-colors text-xs text-white/60 hover:text-white"
            title="Export composite video"
          >
            <Film className="w-4 h-4" />
            <span className="hidden md:inline">Export</span>
          </button>
        {user?.email?.toLowerCase() === process.env.REACT_APP_OWNER_EMAIL?.toLowerCase() && (
          <button
            onClick={onShowAnalytics}
            title="Analytics"
            className="flex items-center gap-2 px-3 h-10 rounded-md border border-white/15 bg-white/[0.03] hover:bg-white/[0.08] transition-colors text-xs text-white/60 hover:text-white"
          >
            <BarChart2 className="w-4 h-4" />
            <span className="hidden md:inline">Analytics</span>
          </button>
        )}
        <button
          onClick={handleLogout}
          title={user?.email ?? "Sign out"}
          className="flex items-center gap-2 px-3 h-10 rounded-md border border-white/15 bg-white/[0.03] hover:bg-white/[0.08] transition-colors text-xs text-white/60 hover:text-white"
        >
          <LogOut className="w-4 h-4" />
          <span className="hidden md:inline">Sign out</span>
        </button>
      </div>
    </header>
  );
}
